import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, Target, Trash2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * GoalDetailModal — view history chart, log a new value, edit target, delete.
 */
export const GoalDetailModal = ({ goal: initialGoal, onClose, onChanged, onDeleted }) => {
  const [goal, setGoal] = useState(initialGoal);
  const [history, setHistory] = useState([]);
  const [newValue, setNewValue] = useState("");
  const [editTarget, setEditTarget] = useState(false);
  const [targetInput, setTargetInput] = useState(String(initialGoal.target_value));
  const [saving, setSaving] = useState(false);

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API}/athlete/goals/${goal.goal_id}/history`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
        setGoal(data.goal);
      }
    } catch (e) {
      console.error("goal history fetch failed", e);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logValue = async () => {
    const num = Number(newValue);
    if (!newValue || Number.isNaN(num)) return toast.error("Enter a number");
    setSaving(true);
    try {
      const res = await fetch(`${API}/athlete/goals/${goal.goal_id}/log`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value: num }),
      });
      if (!res.ok) throw new Error("Failed");
      setNewValue("");
      toast.success("Progress logged. Keep going.");
      await fetchHistory();
      onChanged?.();
    } catch (e) {
      toast.error("Couldn't log — try again");
    } finally {
      setSaving(false);
    }
  };

  const saveTarget = async () => {
    const num = Number(targetInput);
    if (!targetInput || Number.isNaN(num)) return toast.error("Enter a number");
    setSaving(true);
    try {
      const res = await fetch(`${API}/athlete/goals/${goal.goal_id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target_value: num }),
      });
      if (!res.ok) throw new Error("Failed");
      setEditTarget(false);
      toast.success("Target updated");
      await fetchHistory();
      onChanged?.();
    } catch (e) {
      toast.error("Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const deleteGoal = async () => {
    if (!window.confirm("Delete this goal? History will be lost.")) return;
    try {
      const res = await fetch(`${API}/athlete/goals/${goal.goal_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Goal deleted");
      (onDeleted || onChanged)?.();
    } catch (e) {
      toast.error("Couldn't delete");
    }
  };

  const pct = Math.max(0, Math.min(100, goal.progress_pct || 0));
  const isComplete = pct >= 100;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="goal-detail-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-surface border border-border rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-surface">
          <div className="flex items-center gap-2 min-w-0">
            <Target className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <h2 className="font-heading text-lg font-bold text-white truncate">{goal.title}</h2>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-white" data-testid="goal-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Big number */}
          <div className={`rounded-xl p-5 text-center border ${
            isComplete
              ? "bg-emerald-500/10 border-emerald-500/40"
              : "bg-gradient-to-br from-amber-500/10 via-surface to-surface border-amber-500/30"
          }`}>
            <p className="text-xs uppercase tracking-widest text-text-muted font-bold mb-2">Current</p>
            <p className="font-heading text-5xl font-black text-white">
              {formatValue(goal.current_value)} <span className="text-xl text-text-muted font-normal">{goal.unit}</span>
            </p>
            <div className="mt-3 h-2 bg-[#1E293B] rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  isComplete ? "bg-emerald-500" : "bg-amber-500"
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className={`mt-2 text-sm font-bold ${isComplete ? "text-emerald-400" : "text-amber-400"}`} data-testid="goal-distance">
              {goal.distance_label}
            </p>
          </div>

          {/* Log new value */}
          <div>
            <p className="text-xs uppercase tracking-widest text-text-muted font-bold mb-2">Log today's value</p>
            <div className="flex gap-2">
              <input
                type="number"
                step="0.1"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder={`New ${goal.unit}…`}
                className="flex-1 bg-[#0B0F1A] border border-border rounded-lg px-3 py-2.5 text-white placeholder-text-muted focus:border-amber-500/70 focus:outline-none"
                data-testid="goal-log-input"
              />
              <button
                onClick={logValue}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-bold px-5 rounded-lg transition-colors"
                data-testid="goal-log-submit"
              >
                Log
              </button>
            </div>
          </div>

          {/* Target (editable) */}
          <div className="bg-[#0B0F1A] border border-border rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-widest text-text-muted font-bold mb-0.5">Target</p>
              {editTarget ? (
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.1"
                    value={targetInput}
                    onChange={(e) => setTargetInput(e.target.value)}
                    className="flex-1 bg-surface border border-border rounded px-2 py-1 text-sm text-white"
                    data-testid="goal-target-edit-input"
                  />
                  <button onClick={saveTarget} className="text-xs font-bold text-amber-400">Save</button>
                  <button onClick={() => setEditTarget(false)} className="text-xs text-text-muted">Cancel</button>
                </div>
              ) : (
                <p className="text-sm text-white font-bold">{formatValue(goal.target_value)} {goal.unit}</p>
              )}
            </div>
            {!editTarget && (
              <button
                onClick={() => setEditTarget(true)}
                className="text-xs text-text-secondary hover:text-amber-400"
                data-testid="goal-edit-target-btn"
              >
                Edit
              </button>
            )}
          </div>

          {/* Sparkline */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-text-muted" />
                <p className="text-xs uppercase tracking-widest text-text-muted font-bold">History</p>
              </div>
              <span className="text-xs text-text-muted">{history.length} entries</span>
            </div>
            <Sparkline data={history} targetValue={goal.target_value} startValue={goal.start_value} />
          </div>

          {/* Recent entries */}
          {history.length > 1 && (
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {[...history].reverse().slice(0, 8).map((h) => (
                <div key={h.log_id} className="flex items-center justify-between text-sm border-b border-border pb-1.5">
                  <span className="text-white font-semibold">{formatValue(h.value)} {goal.unit}</span>
                  <span className="text-xs text-text-muted">{new Date(h.logged_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}

          {/* Delete */}
          <button
            onClick={deleteGoal}
            className="w-full flex items-center justify-center gap-2 text-xs text-rose-400/70 hover:text-rose-400 py-2 transition-colors"
            data-testid="goal-delete-btn"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete goal
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// =========== Sparkline =============
const Sparkline = ({ data, targetValue, startValue }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-24 bg-[#0B0F1A] border border-border rounded-lg flex items-center justify-center text-xs text-text-muted">
        No history yet
      </div>
    );
  }

  const values = data.map((d) => Number(d.value));
  const allVals = [...values, Number(targetValue), Number(startValue)];
  const min = Math.min(...allVals);
  const max = Math.max(...allVals);
  const range = max - min || 1;

  const w = 320;
  const h = 80;
  const px = (i) => data.length === 1 ? w / 2 : (i / (data.length - 1)) * (w - 20) + 10;
  const py = (v) => h - ((v - min) / range) * (h - 16) - 8;

  const pathD = data.map((d, i) => `${i === 0 ? "M" : "L"} ${px(i)} ${py(d.value)}`).join(" ");
  const targetY = py(Number(targetValue));

  return (
    <div className="bg-[#0B0F1A] border border-border rounded-lg p-2">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24">
        {/* target line */}
        <line x1="0" x2={w} y1={targetY} y2={targetY} stroke="rgba(16, 185, 129, 0.4)" strokeDasharray="3 3" strokeWidth="1" />
        <text x={w - 4} y={targetY - 4} textAnchor="end" fill="rgba(16, 185, 129, 0.7)" fontSize="9">target</text>

        <path d={pathD} fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {data.map((d, i) => (
          <circle key={i} cx={px(i)} cy={py(d.value)} r="2.5" fill="#F59E0B" />
        ))}
      </svg>
    </div>
  );
};

const formatValue = (v) => {
  if (v == null) return "—";
  return Number.isInteger(Number(v)) ? v : Number(v).toFixed(1);
};

export default GoalDetailModal;
